import { useRef, useState } from "react";
import { toast } from "sonner";
import { X, Upload, FileDown, Loader2, CheckCircle2, AlertTriangle, MinusCircle } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import { candidatesFromCsv, CSV_TEMPLATE } from "@/lib/csv";

const KIND_STYLE = { ok: "text-emerald-600", skip: "text-amber-600", error: "text-rose-600" };
const KIND_ICON = { ok: CheckCircle2, skip: MinusCircle, error: AlertTriangle };

function downloadTemplate() {
  const url = URL.createObjectURL(new Blob([CSV_TEMPLATE], { type: "text/csv" }));
  const a = document.createElement("a"); a.href = url; a.download = "candidates-template.csv"; a.click();
  URL.revokeObjectURL(url);
}

function PreviewTable({ rows }) {
  return (
    <div className="max-h-72 overflow-y-auto border border-[color:var(--bd-sub)] rounded-lg" data-testid="bulk-preview-table">
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50 sticky top-0">
          <tr><th className="text-left px-3 py-2">Row</th><th className="text-left px-3 py-2">Candidate</th><th className="text-left px-3 py-2 hidden sm:table-cell">Phone</th><th className="text-left px-3 py-2 hidden sm:table-cell">Status</th></tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const Icon = KIND_ICON[r.kind];
            const st = <span className="inline-flex items-center gap-1"><Icon className="w-3.5 h-3.5 shrink-0" />{r.issue || "Ready to invite"}</span>;
            return (
              <tr key={r.line} className="border-t border-[color:var(--bd-sub)]" data-testid={`bulk-row-${r.line}`}>
                <td className="px-3 py-2 text-slate-400 font-mono text-xs align-top">{r.line}</td>
                <td className="px-3 py-2 min-w-0">
                  <div className="font-medium break-words">{r.name || "—"}</div><div className="text-xs text-slate-500 break-all">{r.email || "—"}</div>
                  <div className={`sm:hidden text-xs mt-1 ${KIND_STYLE[r.kind]}`}>{st}</div>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600 hidden sm:table-cell">{r.phone || "—"}</td>
                <td className={`px-3 py-2 text-xs hidden sm:table-cell ${KIND_STYLE[r.kind]}`} data-testid={`bulk-row-status-${r.line}`}>{st}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ResultSummary({ result, onClose }) {
  const lines = [...result.skipped, ...result.failed];
  return (
    <div data-testid="bulk-result">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat label="Invited" value={result.invited.length} cls="text-emerald-600" testid="bulk-result-invited" />
        <Stat label="Skipped" value={result.skipped.length} cls="text-amber-600" testid="bulk-result-skipped" />
        <Stat label="Failed" value={result.failed.length} cls="text-rose-600" testid="bulk-result-failed" />
      </div>
      {lines.length > 0 && (
        <ul className="max-h-48 overflow-y-auto text-xs space-y-1 mb-4">
          {lines.map((l, i) => <li key={i} className="text-slate-600"><span className="font-medium">{l.email}</span> — {l.reason}</li>)}
        </ul>
      )}
      <div className="flex justify-end"><button data-testid="bulk-done" onClick={onClose} className="px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white">Done</button></div>
    </div>
  );
}

const Stat = ({ label, value, cls, testid }) => (
  <div className="rounded-lg border border-[color:var(--bd-sub)] p-3 text-center">
    <div className={`font-display text-2xl font-bold ${cls}`} data-testid={testid}>{value}</div>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
  </div>
);

export default function BulkInviteDialog({ jobId, phoneStage, existingEmails, onClose, onDone }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const ready = (rows || []).filter(r => r.kind === "ok");

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const parsed = candidatesFromCsv(await f.text(), existingEmails);
      if (!parsed.length) throw new Error("No candidate rows found under the header");
      if (parsed.length > 500) throw new Error("Up to 500 candidates per upload");
      setRows(parsed); setFileName(f.name);
    } catch (err) { toast.error(err.message); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/bulk-invite`, { candidates: ready.map(({ name, email, phone }) => ({ name, email, phone: phone || null })) });
      const skippedLocal = rows.filter(r => r.kind === "skip").map(r => ({ email: r.email, reason: r.issue.replace(" — will be skipped", "") }));
      const failedLocal = rows.filter(r => r.kind === "error").map(r => ({ email: r.email || `Row ${r.line}`, reason: r.issue }));
      setResult({ ...data, skipped: [...skippedLocal, ...data.skipped], failed: [...failedLocal, ...data.failed] });
      toast.success(`Invited ${data.invited.length} candidate${data.invited.length === 1 ? "" : "s"}`);
      onDone();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { if (!busy && !result) onClose(); }}>
      <div onClick={(e) => e.stopPropagation()} className="tp-card p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="bulk-invite-dialog">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-xl font-semibold">Bulk Invite from CSV</h2>
          <button data-testid="bulk-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {result ? <ResultSummary result={result} onClose={onClose} /> : (
          <>
            <p className="text-sm text-slate-500 mb-4">Upload a CSV with <b>name</b>, <b>email</b> and optional <b>phone</b> columns. You'll review every row before any invite goes out.{phoneStage && " Candidates with a phone number are called for the AI phone screen right after inviting."}</p>
            <div className="flex flex-wrap gap-2 mb-4">
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="bulk-file-input" />
              <button data-testid="bulk-choose-file" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white"><Upload className="w-4 h-4" /> {rows ? "Choose another file" : "Choose CSV file"}</button>
              <button data-testid="bulk-template" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] hover:bg-slate-50"><FileDown className="w-4 h-4" /> Download template</button>
            </div>
            {rows && (
              <>
                <div className="text-xs text-slate-500 mb-2" data-testid="bulk-summary">
                  <b>{fileName}</b> · {ready.length} ready · {rows.filter(r => r.kind === "skip").length} skipped · {rows.filter(r => r.kind === "error").length} with errors
                </div>
                <PreviewTable rows={rows} />
                <div className="flex justify-end gap-2 mt-4">
                  <button data-testid="bulk-cancel" onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-800">Cancel</button>
                  <button data-testid="bulk-submit" onClick={submit} disabled={busy || !ready.length} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />} Invite {ready.length} candidate{ready.length === 1 ? "" : "s"}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
